import { UntitledIcon } from '@faceup/icons'
import { ulDownload01 } from '@faceup/icons/ulDownload01'
import { ulXCircle } from '@faceup/icons/ulXCircle'
import { Tooltip } from '@faceup/ui-base'
import {
  type ComponentProps,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ucLoader, ucPauseButton, ucPlayButton } from '../customIcons'
import { UiContext } from '../UiProvider/UiProvider'
import { UnstyledButton } from '../UnstyledButton'

type AudioPlayerWithRemoveProps = {
  onRemove?: () => Promise<void>
  onDownload?: never
}

type AudioPlayerWithDownloadProps = {
  onRemove?: never
  onDownload?: () => Promise<void>
}

export type AudioPlayerProps = {
  duration?: string
  onPlay?: () => Promise<string>
  label?: ReactNode
} & (AudioPlayerWithRemoveProps | AudioPlayerWithDownloadProps)

export const AudioPlayer = ({
  duration,
  onPlay,
  onRemove,
  onDownload,
  label,
}: AudioPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isPlaybackLoading, setIsPlaybackLoading] = useState(false)
  const [isDownloadLoading, setIsDownloadLoading] = useState(false)
  const [isRemoveLoading, setIsRemoveLoading] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [totalDuration, setTotalDuration] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const progressBarRef = useRef<HTMLDivElement | null>(null)
  const { audioPlayer } = useContext(UiContext)
  const translations = audioPlayer ?? {
    label: 'Audio recording',
    play: 'Play',
    pause: 'Pause',
    playDisabled: 'Playback not available',
    download: 'Download',
    remove: 'Remove',
  }

  const formatTime = (seconds: number): string => {
    if (!Number.isFinite(seconds)) {
      return '0:00'
    }
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const updateTime = useMemo(
    () => (clientX: number) => {
      const audio = audioRef.current
      const progressBar = progressBarRef.current
      if (!audio || !totalDuration || !progressBar) {
        return
      }

      const rect = progressBar.getBoundingClientRect()
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width))
      const percentage = x / rect.width
      const newTime = percentage * totalDuration
      audio.currentTime = newTime
      setCurrentTime(newTime)
    },
    [totalDuration]
  )

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true)
    updateTime(e.clientX)
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        updateTime(e.clientX)
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
    }

    return
  }, [isDragging, updateTime])

  const handlePlayback = async () => {
    const audio = audioRef.current

    if (!audio) {
      return
    }

    if (onPlay && !audio.src) {
      setIsPlaybackLoading(true)
      try {
        const newSource = await onPlay()
        audio.src = newSource
      } finally {
        setIsPlaybackLoading(false)
      }
    }

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      try {
        await audio.play()
        setIsPlaying(true)
      } catch (error) {
        setIsPlaying(false)
        console.error('Error playing audio:', error)
        // download instead if playback fails
        handleDownload()
      }
    }
  }

  const handleDownload = async () => {
    if (!onDownload) {
      return
    }
    setIsDownloadLoading(true)
    try {
      await onDownload()
    } finally {
      setIsDownloadLoading(false)
    }
  }

  const handleRemove = async () => {
    if (!onRemove) {
      return
    }
    setIsRemoveLoading(true)
    try {
      await onRemove()
    } finally {
      setIsRemoveLoading(false)
    }
  }

  return (
    <div
      className={
        'flex w-fit items-center gap-3 rounded-full bg-colorPrimaryBg p-2 pe-3 transition-colors hover:bg-colorPrimaryBgHover'
      }
    >
      <div className='flex grow items-center gap-2'>
        <audio
          ref={audioRef}
          onEnded={() => setIsPlaying(false)}
          onTimeUpdate={e => setCurrentTime(e.currentTarget.currentTime)}
          onLoadedMetadata={e => setTotalDuration(e.currentTarget.duration)}
        />
        <IconButtonWithTooltip
          label={
            !onPlay ? translations.playDisabled : isPlaying ? translations.pause : translations.play
          }
          onClick={handlePlayback}
          disabled={!onPlay}
        >
          <UntitledIcon
            icon={isPlaybackLoading ? ucLoader : isPlaying ? ucPauseButton : ucPlayButton}
            size={20}
            isSpinning={isPlaybackLoading}
          />
        </IconButtonWithTooltip>
        <div className='max-w-[250px] truncate font-semibold text-colorPrimary text-sm'>
          {label ?? translations.label}
        </div>
      </div>

      <div className='flex grow-0 items-center gap-2'>
        {totalDuration > 0 && (
          <div className='flex items-center gap-2'>
            <span className='whitespace-nowrap font-medium text-colorTextSecondary text-xs tabular-nums'>
              {formatTime(currentTime)}
            </span>
            <div
              ref={progressBarRef}
              className='group h-1 w-24 cursor-pointer select-none rounded-full bg-colorBorder transition-all hover:h-2'
              onMouseDown={handleMouseDown}
            >
              <div
                className='h-full rounded-full bg-colorPrimary transition-colors group-hover:bg-colorPrimaryHover'
                style={{ width: `${(currentTime / totalDuration) * 100}%` }}
              />
            </div>
            <span className='whitespace-nowrap font-medium text-colorTextSecondary text-xs tabular-nums'>
              {duration ?? formatTime(totalDuration)}
            </span>
          </div>
        )}
        {totalDuration === 0 && duration && (
          <span className='font-medium text-colorTextSecondary text-xs'>{duration}</span>
        )}

        {onDownload && (
          <IconButtonWithTooltip
            label={translations.download}
            onClick={handleDownload}
            disabled={isDownloadLoading}
          >
            <UntitledIcon
              icon={isDownloadLoading ? ucLoader : ulDownload01}
              size={12}
              isSpinning={isDownloadLoading}
            />
          </IconButtonWithTooltip>
        )}

        {onRemove && (
          <IconButtonWithTooltip label={translations.remove} onClick={handleRemove}>
            <UntitledIcon
              icon={isRemoveLoading ? ucLoader : ulXCircle}
              size={12}
              isSpinning={isRemoveLoading}
            />
          </IconButtonWithTooltip>
        )}
      </div>
    </div>
  )
}

type IconButtonWithTooltipProps = ComponentProps<typeof UnstyledButton> & {
  label: string
}

const IconButtonWithTooltip = ({ label, ...props }: IconButtonWithTooltipProps) => {
  return (
    <Tooltip title={label}>
      <UnstyledButton
        className='text-colorPrimary leading-0 transition-colors hover:text-colorPrimaryHover disabled:cursor-not-allowed disabled:opacity-50'
        {...props}
      />
    </Tooltip>
  )
}
